import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, siteConfig } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen bg-background-50 pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-400 hover:text-primary-300">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen bg-background-50 pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb
            items={[
              { label: '文章', href: '/article/top-10-japanese-games-2026' },
              { label: article.category },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-2.5 py-0.5 bg-accent-500/15 text-accent-400 text-xs rounded font-medium">
                {article.category}
              </span>
              <span className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}分钟阅读
              </span>
              <span className="text-xs text-foreground-500">{article.publishDate}</span>
              <span className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 font-bold leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full border border-background-300/30">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-invert max-w-none
              prose-headings:text-foreground-900 prose-headings:font-heading
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-foreground-600 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-sm md:prose-p:text-base
              prose-strong:text-foreground-800
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-300/30">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">分享到：</span>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </a>
              </div>
              <Link
                to="/"
                className="text-sm text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-1"
              >
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 font-bold mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.publishDate,
            image: article.coverImage,
            publisher: {
              '@type': 'Organization',
              name: siteConfig.name,
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${siteConfig.url}/article/${article.slug}`,
            },
          }),
        }}
      />
    </>
  );
}